import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap, withLatestFrom } from 'rxjs/operators';

import { DocumentListActions } from './document-list.actions';
import { DocumentListSelectors } from './document-list.selectors';
import {
  UploadDocumentAdminModalComponent,
  UploadDocumentAdminModalData,
} from '../../features/document/components/upload-document-admin-modal/upload-document-admin-modal.component';
import { documentAssignee } from '../../features/document/constants/document-assignee';
import { DocumentTypeExtensionMapper } from '../../features/document/constants/document-type.mappers';
import { DocumentType } from '../../features/document/models/uploaded-document.model';
import { DocumentService } from '../../features/document/services/document.service';
import { TransactionDocumentService } from '../../features/document/services/transaction-document.service';
import { WebSocketEntities } from '../../features/shared/models/web-socket/web-socket.models';
import { createWebSocketEffect } from '../../features/shared/services/web-socket/create-web-socket-effect';
import { WebSocketService } from '../../features/shared/services/web-socket/web-socket.service';
import {
  createNotificationEffect,
  PushNotificationRecord,
} from '../../features/shared/utils/create-notification-effect';
import { reportEffectError } from '../../features/shared/utils/report-effect-error';
import { RootState } from '../root-state.model';
import { TransactionActions } from '../transaction/transaction.actions';
import { TransactionSelectors } from '../transaction/transaction.selectors';
import { UserProfileSelectors } from '../user-profile/user-profile.selectors';

@Injectable()
export class DocumentListEffects {
  private service = inject(TransactionDocumentService);
  private readonly documentService = inject(DocumentService);
  private actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly webSocketService = inject(WebSocketService);
  private readonly dialog = inject(MatDialog);

  private readonly notifications: PushNotificationRecord = {
    [DocumentListActions.DeleteItemFail.type]: {
      message: 'Failed to delete the document',
      isSuccess: false,
    },
    [DocumentListActions.SaveDocumentFail.type]: {
      message: 'Failed to update the document',
      isSuccess: false,
    },
    [DocumentListActions.DeleteItemSuccess.type]: { message: 'Document deleted', isSuccess: true },
    [DocumentListActions.SaveDocumentSuccess.type]: {
      message: 'Document updated',
      isSuccess: true,
    },
    [DocumentListActions.SendEmailStatusSuccess.type]: {
      message: 'Status update email sent',
      isSuccess: true,
    },
    [DocumentListActions.SendEmailStatusFail.type]: {
      message: 'Failed to send status update',
      isSuccess: false,
    },
    [DocumentListActions.CreateIncomingDocumentSuccess.type]: {
      message: 'Incoming document created',
      isSuccess: true,
    },
    [DocumentListActions.CreateIncomingDocumentFail.type]: {
      message: 'Failed to create incoming document',
      isSuccess: false,
    },
    [DocumentListActions.CreateIncomingDocumentsSuccess.type]: {
      message: 'Incoming documents created',
      isSuccess: true,
    },
    [DocumentListActions.CreateIncomingDocumentsFail.type]: {
      message: 'Failed to create incoming documents',
      isSuccess: false,
    },
    [DocumentListActions.OcrDocumentFail.type]: {
      message: 'Failed to OCR the document',
      isSuccess: false,
    },
    [DocumentListActions.OcrDocumentSuccess.type]: { message: "Document OCR'd", isSuccess: true },
    [DocumentListActions.RequestFlashExtractionSuccess.type]: {
      message: 'Flash extraction requested',
      isSuccess: true,
    },
    [DocumentListActions.RequestFlashExtractionFail.type]: {
      message: 'Failed to request flash extraction',
      isSuccess: false,
    },
    [DocumentListActions.UploadDocumentSuccess.type]: {
      message: 'Document uploaded',
      isSuccess: true,
    },
  };

  notificationEffect$: Observable<Action> = createNotificationEffect(this.actions$, this.notifications);

  uploadDocumentEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentListActions.UploadDocument),
      withLatestFrom(this.store),
      switchMap(([action, store]) => {
        return this.dialog
          .open<UploadDocumentAdminModalComponent, UploadDocumentAdminModalData>(UploadDocumentAdminModalComponent, {
            data: {
              document: action.document,
            },
            panelClass: 'upload-document-admin-modal',
            minWidth: '40vw',
            minHeight: '40vh',
          })
          .afterClosed()
          .pipe(
            filter((result) => !!result),
            map((result) => DocumentListActions.UploadDocumentSuccess({ document: action.document })),
            reportEffectError(DocumentListActions.UploadDocumentFail)
          );
      }),
      reportEffectError(DocumentListActions.UploadDocumentFail)
    )
  );

  loadListEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DocumentListActions.LoadList,
        DocumentListActions.CreateIncomingDocumentSuccess,
        DocumentListActions.CreateIncomingDocumentsSuccess,
        DocumentListActions.RequestFlashExtractionSuccess,
        // A rejected request means the row was stale — reload so the availability resyncs.
        DocumentListActions.RequestFlashExtractionFail
      ),
      withLatestFrom(this.store),
      switchMap(([action, store]) => {
        return this.service
          .getUploadedDocuments(
            store.documentList.searchFilter,
            store.documentList.sort,
            store.documentList.search,
            store.documentList.paginationInfo.currentPage
          )
          .pipe(
            map((response) =>
              DocumentListActions.LoadListSuccess({
                documents: response.items,
                paginationInfo: {
                  total: response.total,
                  currentPage: response.currentPage,
                  pageSize: response.pageSize,
                  isLastPage: response.isLastPage,
                },
              })
            ),
            reportEffectError(DocumentListActions.LoadListFail)
          );
      })
    )
  );

  loadCountEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DocumentListActions.LoadList,
        DocumentListActions.CreateIncomingDocumentSuccess,
        DocumentListActions.CreateIncomingDocumentsSuccess
      ),
      withLatestFrom(this.store),
      switchMap(([action, store]) => {
        return this.service.getTotalCount().pipe(
          map((count) =>
            DocumentListActions.LoadCountSuccess({
              count,
            })
          ),
          reportEffectError(DocumentListActions.LoadCountFail)
        );
      })
    )
  );

  loadNextChunkEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentListActions.LoadNextChunk),
      withLatestFrom(this.store),
      filter(([action, store]) => !store.documentList.paginationInfo.isLastPage),
      switchMap(([action, store]) => {
        return this.service
          .getUploadedDocuments(
            store.documentList.searchFilter,
            store.documentList.sort,
            store.documentList.search,
            store.documentList.paginationInfo.currentPage + 1
          )
          .pipe(
            map((response) =>
              DocumentListActions.LoadNextChunkSuccess({
                documents: response.items,
                paginationInfo: {
                  total: response.total,
                  currentPage: response.currentPage,
                  pageSize: response.pageSize,
                  isLastPage: response.isLastPage,
                },
              })
            ),
            reportEffectError(DocumentListActions.LoadNextChunkFail)
          );
      })
    )
  );

  loadDocumentHashLookupEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentListActions.LoadDocumentHashLookup),
      withLatestFrom(this.store, this.store.select(TransactionSelectors.listLookup)),
      switchMap(([action, store, transactionLookup]) =>
        this.documentService.getCommonDocumentsByDocumentHash(action.documentHash).pipe(
          map((documents) =>
            DocumentListActions.LoadDocumentHashLookupSuccess({
              documentHashMatches: documents,
            })
          ),
          reportEffectError(DocumentListActions.LoadDocumentHashLookupFail)
        )
      )
    )
  );

  loadTransactionSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.LoadTransactionsSuccess),
      filter((action) => action.trigger === 'documents'),
      map(() => DocumentListActions.LoadList())
    )
  );

  publishTransactionSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionActions.PublishTransactionSuccess),
      filter((action) => action.trigger === 'documents'),
      map(() => DocumentListActions.LoadList())
    )
  );

  deleteItemEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentListActions.DeleteItem),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.service.deleteUploadedDocument(action.document?.uuid).pipe(
          map((document) =>
            DocumentListActions.DeleteItemSuccess({
              document,
              withNotification: action.withNotification,
            })
          ),
          reportEffectError(DocumentListActions.DeleteItemFail)
        )
      )
    )
  );

  ocrItemEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentListActions.OcrDocument),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.service.ocrDocument(action.document?.id).pipe(
          map((document) => DocumentListActions.OcrDocumentSuccess()),
          reportEffectError(DocumentListActions.OcrDocumentFail)
        )
      )
    )
  );

  requestFlashExtractionEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentListActions.RequestFlashExtraction),
      switchMap((action) =>
        this.service.requestFlashExtraction(action.document?.id).pipe(
          map(() => DocumentListActions.RequestFlashExtractionSuccess()),
          reportEffectError(DocumentListActions.RequestFlashExtractionFail)
        )
      )
    )
  );

  saveDocumentEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentListActions.SaveDocument),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.service.updateUploadedDocument(action.document).pipe(
          map((document) => DocumentListActions.SaveDocumentSuccess({ document })),
          reportEffectError(DocumentListActions.SaveDocumentFail)
        )
      )
    )
  );

  createIncomingDocumentEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentListActions.CreateIncomingDocument),
      withLatestFrom(this.store.select(UserProfileSelectors.email)),
      switchMap(([action, email]) => {
        const myAssignee = documentAssignee.find((a) => a.email === email);
        const stream$ = myAssignee
          ? this.service
              .updateUploadedDocument({
                id: action.documentId,
                responsible: myAssignee.text,
                internalStatus: undefined,
                status: undefined,
              })
              .pipe(
                map(() =>
                  DocumentListActions.CreateIncomingDocumentSuccess({
                    documentId: action.documentId,
                  })
                ),
                reportEffectError(DocumentListActions.CreateIncomingDocumentFail)
              )
          : this.service.createIncomingDocument(action.documentId).pipe(
              map(() => DocumentListActions.CreateIncomingDocumentSuccess({ documentId: action.documentId })),
              reportEffectError(DocumentListActions.CreateIncomingDocumentFail)
            );

        return stream$;
      })
    )
  );

  createIncomingDocumentsEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentListActions.CreateIncomingDocuments),
      withLatestFrom(this.store, this.store.select(DocumentListSelectors.documentWithLibraries)),
      switchMap(([action, store, orderedAndSortedDocuments]) => {
        const documentIds = orderedAndSortedDocuments.filter((d) => !d.hasIncomingDocument).map((d) => d.id);
        const stream$ = documentIds.length ? this.service.createIncomingDocuments(documentIds) : of(true);

        return stream$.pipe(
          map(() => DocumentListActions.CreateIncomingDocumentsSuccess({ documentIds })),
          reportEffectError(DocumentListActions.CreateIncomingDocumentsFail)
        );
      })
    )
  );

  downloadEffect$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentListActions.Download),
        switchMap((action) => {
          const documentType =
            action.event.type ??
            (Object.keys(action.event.document.uploaded).find(
              (key) => !!action.event.document.uploaded[key]
            ) as DocumentType);

          switch (documentType) {
            case DocumentType.DOCX:
              return this.service.downloadFile(
                action.event.document.tenantShortName,
                `${action.event.document.uuid}.docx`
              );
            case DocumentType.PDF:
              return this.service.downloadFile(
                action.event.document.tenantShortName,
                `${action.event.document.uuid}.pdf`
              );
            case DocumentType.DOCX_CURATED:
              return this.service.downloadFile(
                action.event.document.tenantShortName,
                `${action.event.document.uuid}-curated.docx`
              );

            case DocumentType.PDF_DECRYPTED:
              return this.service.downloadFile(
                action.event.document.tenantShortName,
                `${action.event.document.uuid}.dec.pdf`
              );

            case DocumentType.OCR_PDF:
              return this.service.downloadFile(
                action.event.document.tenantShortName,
                `${action.event.document.uuid}_ocr.pdf`
              );

            case DocumentType.DOCX_CUR0:
              return this.service.downloadFile(
                action.event.document.tenantShortName,
                `${action.event.document.uuid}-acro-cur0.docx`
              );

            case DocumentType.DOCX_CUR1:
              return this.service.downloadFile(
                action.event.document.tenantShortName,
                `${action.event.document.uuid}-acro-cur1.docx`
              );

            case DocumentType.HTML:
              return this.service.downloadFile(
                action.event.document.tenantShortName,
                `${action.event.document.uuid}.html`
              );

            default:
              return of(null);
          }
        })
      ),
    { dispatch: false }
  );

  sendEmailStatusEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentListActions.SendEmailStatus),
      withLatestFrom(this.store.select(TransactionSelectors.listLookup)),
      switchMap(([action, transactionLookup]) =>
        this.service
          .sendEmail(
            action.document.createdBy,
            transactionLookup[action.document.transactionId]?.transactionDisplayName,
            action.document.status,
            action.document.uploadedFile,
            `${window.location.origin}/documents?documentId=${action.document.id}`,
            action.template
          )
          .pipe(
            map((document) => DocumentListActions.SendEmailStatusSuccess()),
            reportEffectError(DocumentListActions.SendEmailStatusFail)
          )
      )
    )
  );

  wsEffect$: Observable<Action> = createWebSocketEffect(
    this.store,
    this.actions$,
    this.webSocketService,
    WebSocketEntities.Upload,
    () => DocumentListActions.LoadList()
  );
}
